import { useContext } from "react";

import { Group, Stack, StackProps } from "@mantine/core";

import { IconReportMedical } from "@tabler/icons-react";

import { TextWrapper } from "@/components/TextWrapper";
import { ProfileContext } from "@/contexts/profile";
import { usePractitionerPermissions } from "@/permission/hooks/usePractitionerPermissions";

import { NoDataContainer } from "../../NoDataContainer";

export enum EncounterProceduresNoDataVariant {
  border = "1px solid var(--mantine-color-cream-4)",
  noBorder = "none",
}

type EncounterProceduresNoDataProps<T> = Omit<StackProps, "children"> & {
  open: (id: T) => void;
  drawerName: T;
  isEncounterActive: boolean;
  isAmendment: boolean;
  variant?: EncounterProceduresNoDataVariant;
  allowBillerRole?: boolean;
  title?: string;
};

const EncounterProceduresNoData = <T,>({
  open,
  drawerName,
  isEncounterActive,
  isAmendment,
  variant = EncounterProceduresNoDataVariant.border,
  allowBillerRole = false,
  title = "Procedures",
  ...stackProps
}: EncounterProceduresNoDataProps<T>) => {
  const { isPatient } = useContext(ProfileContext);
  const { hasProviderOrScribeRole, hasBillerRole } = usePractitionerPermissions();
  const canAddProcedures =
    !isPatient && (hasProviderOrScribeRole || (allowBillerRole && hasBillerRole));

  const borderValue = variant === EncounterProceduresNoDataVariant.noBorder ? undefined : variant;

  return (
    <Stack gap="xs" {...stackProps}>
      <TextWrapper c="dark.9" variant="bold">
        {title}
      </TextWrapper>
      <Group p="md" bg="white" bd={borderValue} justify="space-between" style={{ borderRadius: 8 }}>
        <NoDataContainer
          text="No procedures have been added."
          onAnchorClick={() => open(drawerName)}
          actionTestId="encounter-add-procedure-button"
          hideAnchor={!canAddProcedures || (!isEncounterActive && !isAmendment)}
          icon={<IconReportMedical size={18} color="var(--mantine-color-gray-8)" />}
          linkText={(isEncounterActive || isAmendment) && canAddProcedures ? "Add Procedure" : ""}
        />
      </Group>
    </Stack>
  );
};

export default EncounterProceduresNoData;
